// ui/src/components/shared/PlanVersionCompare.jsx

/**
 * @file PlanVersionCompare.jsx
 * @description Side-by-side version comparison panel for plan configuration
 *   deltas. Renders a togglable table of field-level changes (modified, added,
 *   removed) grouped by plan section. Consumes delta payload from ep 31
 *   (GET /api/v1/plans/{id}/version-compare). No API calls — prop-driven.
 * @module shared
 * @author data-fit LLC · DecisionFlow · S775
 * @see api_spec_fileB_v4_final.md §2.3 — version-compare response (ep 31)
 * @migration OI-S774-VERSION-DELTA-PAYLOAD — VersionDeltaPayload shape not yet
 *   formally defined in spec. Wire to router sections[] shape when resolved.
 *
 * @changelog
 *   S815  Safe defaults added for all props. Dual alias: accepts baseVersion/
 *         compareVersion (AmendmentApprovalReview shape) alongside canonical
 *         baseVersionLabel/compareVersionLabel. Prevents delta.filter() crash
 *         when called without delta prop.
 */

import { useId } from 'react';

/**
 * @typedef {{
 *   sectionKey: string,
 *   sectionLabel: string,
 *   changes: Array<{
 *     fieldKey: string,
 *     fieldLabel: string,
 *     before: string | null,
 *     after: string,
 *     changeType: 'modified' | 'added' | 'removed'
 *   }>
 * }} DeltaSection
 */

/**
 * Resolves a version label from either a string label, an object with
 * a label/version property, or a bare number.
 */
function resolveVersionLabel(labelProp, objectProp, fallback) {
  if (labelProp != null) return String(labelProp);
  if (objectProp != null) {
    if (typeof objectProp === 'string') return objectProp;
    if (typeof objectProp === 'object') {
      return objectProp.label ?? (objectProp.version != null ? `V${objectProp.version}` : fallback);
    }
    if (typeof objectProp === 'number') return `V${objectProp}`;
  }
  return fallback;
}

/**
 * @param {{
 *   active: boolean,
 *   onToggle: () => void,
 *   baseVersionLabel?: string,
 *   compareVersionLabel?: string,
 *   baseVersion?: object|number|string,
 *   compareVersion?: object|number|string,
 *   delta?: DeltaSection[],
 *   effectiveDateNote?: string,
 *   totalChanges?: number,
 *   className?: string
 * }} props
 */
export default function PlanVersionCompare({
  active,
  onToggle,
  baseVersionLabel,
  compareVersionLabel,
  baseVersion,
  compareVersion,
  delta = [],
  effectiveDateNote,
  totalChanges = 0,
  className = '',
}) {
  // WHY: AmendmentApprovalReview passes baseVersion/compareVersion (objects or numbers).
  // PlanVersionDeltaSummary passes baseVersionLabel/compareVersionLabel (strings).
  // resolveVersionLabel handles both conventions without touching either call site.
  const resolvedBaseLabel    = resolveVersionLabel(baseVersionLabel,    baseVersion,    'Current');
  const resolvedCompareLabel = resolveVersionLabel(compareVersionLabel, compareVersion, 'Proposed');

  const panelId = useId();

  // WHY: delta may be null/undefined when called from amendment review before
  // endpoint returns data. Default [] prevents delta.filter() crash.
  const safeDelta = Array.isArray(delta) ? delta : [];

  const changedSections = safeDelta.filter(
    (section) => Array.isArray(section.changes) && section.changes.length > 0,
  );

  return (
    <div className={className}>
      <button
        type="button"
        className="secondary-button"
        aria-expanded={active}
        aria-controls={panelId}
        onClick={onToggle}
      >
        {active ? 'Hide comparison' : `Compare with ${resolvedBaseLabel}`} ↕
      </button>

      {active && (
        <section
          id={panelId}
          role="region"
          aria-label="Version comparison"
          className={[
            'mt-3 overflow-hidden rounded-[12px]',
            'border border-[rgb(var(--color-border))]',
            'bg-[rgb(var(--color-surface))]',
          ].join(' ')}
        >
          <div
            className={[
              'grid grid-cols-[minmax(140px,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_90px]',
              'border-b border-[rgb(var(--color-border))]',
              'bg-[rgb(var(--color-surface-muted))]',
              'px-4 py-2',
            ].join(' ')}
          >
            <div
              className="text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]"
              aria-hidden="true"
            >
              Field
            </div>

            <div className="border-l border-[rgb(var(--color-border))] pl-4 text-[11px] font-semibold text-[rgb(var(--color-text))]">
              {resolvedBaseLabel}
              <span className="ml-1 font-normal text-[rgb(var(--color-text-muted))]">
                · approved evidence
              </span>
            </div>

            <div className="border-l border-[rgb(var(--color-border))] pl-4 text-[11px] font-semibold text-[rgb(var(--color-text))]">
              {resolvedCompareLabel}
              <span className="ml-1 font-normal text-[rgb(var(--color-text-muted))]">
                · proposed state
              </span>
            </div>

            <div className="pl-3 text-[10px] font-semibold uppercase tracking-wide text-[rgb(var(--color-text-muted))]">
              Change
            </div>
          </div>

          {effectiveDateNote && (
            <div
              role="note"
              aria-label={`Effective event-date resolution rule: ${effectiveDateNote}`}
              className="modifier-manager-info m-4 text-[12px]"
            >
              <strong>Event-date resolution:</strong> {effectiveDateNote}
            </div>
          )}

          {changedSections.length === 0 ? (
            <div className="px-4 py-6">
              <p className="text-[13px] font-semibold text-[rgb(var(--color-text))]">
                No configuration changes detected
              </p>
              <p className="mt-1 text-[12px] text-[rgb(var(--color-text-muted))]">
                Metadata or assignment-scope updates may still be present outside
                this configuration delta.
              </p>
            </div>
          ) : (
            <div>
              <div className="sr-only" aria-live="polite">
                {totalChanges} configuration changes
              </div>

              {changedSections.map((section) => (
                <section
                  key={section.sectionKey}
                  aria-labelledby={`${panelId}-${section.sectionKey}`}
                  className="border-t first:border-t-0 border-[rgb(var(--color-border))]"
                >
                  <div
                    id={`${panelId}-${section.sectionKey}`}
                    className="bg-[rgb(var(--color-bg))] px-4 py-2 text-[12px] font-semibold text-[rgb(var(--color-text))]"
                  >
                    {section.sectionLabel}
                    <span className="ml-2 text-[11px] font-normal text-[rgb(var(--color-text-muted))]">
                      {section.changes.length}{' '}
                      {section.changes.length === 1 ? 'change' : 'changes'}
                    </span>
                  </div>

                  {section.changes.map((change) => (
                    <ChangeRow
                      key={`${section.sectionKey}-${change.fieldKey}`}
                      change={change}
                    />
                  ))}
                </section>
              ))}
            </div>
          )}
        </section>
      )}
    </div>
  );
}

function ChangeRow({ change }) {
  const isModified = change.changeType === 'modified';
  const isAdded = change.changeType === 'added';
  const isRemoved = change.changeType === 'removed';

  const typeLabel = {
    modified: 'Modified',
    added: 'Added',
    removed: 'Removed',
  }[change.changeType];

  return (
    <div
      className={[
        'grid grid-cols-[minmax(140px,0.8fr)_minmax(0,1fr)_minmax(0,1fr)_90px]',
        'border-t border-[rgb(var(--color-border))]',
        'px-4 py-3 text-[12px]',
      ].join(' ')}
    >
      <div className="pr-3 font-medium text-[rgb(var(--color-text))]">
        {change.fieldLabel}
      </div>

      <div className="border-l border-[rgb(var(--color-border))] pl-4">
        {isAdded || change.before === null ? (
          <span
            aria-label="No previous value"
            className="text-[rgb(var(--color-text-muted))]"
          >
            —
          </span>
        ) : (
          <span
            aria-label={`Previous value was ${change.before}`}
            className={[
              isModified ? 'line-through' : '',
              isRemoved
                ? 'text-[rgb(var(--color-danger))]'
                : 'text-[rgb(var(--color-text-muted))]',
            ].join(' ')}
          >
            {change.before}
          </span>
        )}
      </div>

      <div className="border-l border-[rgb(var(--color-border))] pl-4">
        {isRemoved ? (
          <span
            aria-label="Value removed"
            className="text-[rgb(var(--color-text-muted))]"
          >
            —
          </span>
        ) : (
          <span
            className={[
              'font-semibold',
              isAdded
                ? 'text-[rgb(var(--color-success))]'
                : 'text-[rgb(var(--color-primary))]',
            ].join(' ')}
          >
            {change.after}
          </span>
        )}
      </div>

      <div className="pl-3">
        <span
          aria-label={`Change type: ${typeLabel}`}
          className={[
            'inline-flex rounded-[999px] px-2 py-0.5',
            'text-[10px] font-semibold',
            isAdded
              ? 'bg-[rgb(var(--color-success)/0.1)] text-[rgb(var(--color-success))]'
              : isRemoved
                ? 'bg-[rgb(var(--color-danger)/0.1)] text-[rgb(var(--color-danger))]'
                : 'bg-[rgb(var(--color-primary)/0.1)] text-[rgb(var(--color-primary))]',
          ].join(' ')}
        >
          {typeLabel}
        </span>
      </div>
    </div>
  );
}
